import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CardDisclosure } from '@components/card-disclosure';
import { INFO, WARNING } from '@components/card-disclosure/src/card-disclosure.type';
import { IconWarning } from '@components/icons';
import { SectionDisclosure } from '@components/section-disclosure';
import { SortableList } from '@components/sortable-list';
import { SwitchToggle } from '@components/switch-toggle';
import { MULTIPLE_CHOICE_ADDON, SINGLE_CHOICE_ADDON } from '@constants/admissions';
import { closestCenter } from '@dnd-kit/core';
import { Addon } from '@domain/addon';
import classNames from 'classnames';

import { AddOnRow } from './add-on-row';
import { AddOnsSectionProps, AddOnsSectionValue } from './add-ons-section.type';

export const ADD_ONS_SECTION_TEST_ID = 'add-ons-section';

const testId = (prefix: string, suffix: string) => `${prefix}-${suffix}`;

const computeNextSelection = (current: string[], toggledId: string): string[] => {
  if (current.includes(toggledId)) {
    return current.filter((id) => id !== toggledId);
  }
  return [...current, toggledId];
};

const Skeleton = () => (
  <div className='animate-pulse flex flex-col gap-3 py-4 md:py-6' data-testid='add-ons-section-skeleton'>
    {[0, 1, 2].map((index) => (
      <div key={index} className='h-14 bg-tertiary rounded-sm' />
    ))}
  </div>
);

interface SettingToggleProps {
  title: string;
  subtitle: string;
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  dataTestId?: string;
}

const SettingToggle = ({ title, subtitle, value, onChange, disabled, dataTestId }: SettingToggleProps) => (
  <div data-testid={dataTestId} className='flex gap-3 items-start'>
    <SwitchToggle active={value} loading={false} disabled={disabled} onToggleSwitch={(next) => onChange(next)} />
    <div className='flex flex-col gap-0.5 flex-1 min-w-0'>
      <span className='text-subtitle-s text-primary'>{title}</span>
      <span className='text-body-xs text-secondary'>{subtitle}</span>
    </div>
  </div>
);

export const AddOnsSection = ({
  value,
  onChange,
  open,
  onToggleOpen,
  disabled = false,
  availableAddOns,
  isLoading = false,
  onOpenPresetsModal,
  hiddenReason,
  errors,
  dataTestId = ADD_ONS_SECTION_TEST_ID,
}: AddOnsSectionProps) => {
  const { t } = useTranslation();

  const update = <K extends keyof AddOnsSectionValue>(key: K, next: AddOnsSectionValue[K]) => {
    onChange({ [key]: next });
  };

  const selectedAddOns = useMemo<Addon[]>(() => {
    const byId = new Map(availableAddOns.map((addon) => [addon.id, addon]));
    return value.selectedAddOnIds.map((id) => byId.get(id)).filter((addon): addon is Addon => addon !== undefined);
  }, [availableAddOns, value.selectedAddOnIds]);

  const unselectedAddOns = useMemo<Addon[]>(
    () => availableAddOns.filter((addon) => !value.selectedAddOnIds.includes(addon.id)),
    [availableAddOns, value.selectedAddOnIds]
  );

  const handleToggleAddOn = (addonId: string) => {
    update('selectedAddOnIds', computeNextSelection(value.selectedAddOnIds, addonId));
  };

  const handleReorderSelected = (reordered: Addon[]) => {
    update(
      'selectedAddOnIds',
      reordered.map((addon) => addon.id)
    );
  };

  const renderHidden = () =>
    hiddenReason && (
      <CardDisclosure
        titleText={hiddenReason.title}
        descriptionText={hiddenReason.description}
        showChevronIcon
        variant={WARNING}
        isDefaultOpen
        paddingDescription='pt-2'
      />
    );

  const renderEmptyState = () => (
    <button
      type='button'
      className='w-full md:w-111.5 py-2 px-4 rounded-sm fill-tertiary text-action-button-s text-center text-primary focus:outline-hidden focus:ring-0 dark:text-white'
      onClick={onOpenPresetsModal}
      disabled={disabled}
      data-testid={testId(dataTestId, 'add-from-presets-empty')}>
      {t('offers:admissionAddOnsAddFromPresets')}
    </button>
  );

  const renderList = () => (
    <div className='flex flex-col md:flex-row justify-between gap-6' data-testid={testId(dataTestId, 'content')}>
      <div className='flex flex-col gap-3 w-full md:w-111.5'>
        {errors?.mandatoryNoSelection && (
          <CardDisclosure
            titleText={t('offers:admissionAddOnsMandatoryTitle')}
            descriptionText={errors.mandatoryNoSelection}
            paddingDescription='pl-8'
            marginClassNames='mb-4'
            showChevronIcon
            icon={{
              icon: IconWarning,
              height: 'h-6',
              width: 'w-6',
              props: { strokeWidth: '5' },
            }}
            isDefaultOpen
            variant={INFO}
          />
        )}

        <div className='flex flex-col gap-2 p-2 rounded-lg border border-extra-soft bg-primary shadow-[0_2px_8px_0_rgba(0,0,0,0.10)]'>
          {selectedAddOns.length > 0 && (
            <SortableList<Addon>
              items={selectedAddOns}
              collisionDetection={closestCenter}
              onReorder={handleReorderSelected}
              getItemId={(addon) => addon.id}
              className='gap-2!'
              isOverlayActive={false}
              renderItem={(addon, dragHandle) => (
                <AddOnRow
                  addon={addon}
                  checked
                  onToggle={handleToggleAddOn}
                  disabled={disabled}
                  dragHandle={dragHandle}
                  dataTestId={testId(dataTestId, `row-${addon.id}`)}
                />
              )}
            />
          )}

          {unselectedAddOns.map((addon) => (
            <AddOnRow
              key={addon.id}
              addon={addon}
              checked={false}
              onToggle={handleToggleAddOn}
              disabled={disabled}
              dataTestId={testId(dataTestId, `row-${addon.id}`)}
            />
          ))}

          <button
            type='button'
            className='self-stretch py-2 px-4 rounded-sm fill-tertiary text-action-button-s text-center text-primary focus:outline-hidden focus:ring-0 dark:text-white'
            onClick={onOpenPresetsModal}
            disabled={disabled}
            data-testid={testId(dataTestId, 'add-from-presets')}>
            {t('offers:admissionAddOnsAddFromPresets')}
          </button>
        </div>
      </div>

      <div className='flex flex-col gap-6 w-full md:w-61 text-left'>
        <p className='text-secondary text-subtitle-m'>{t('offers:admissionAddOnsSettingsTitle')}</p>

        <SettingToggle
          value={value.hasMandatoryAddOns}
          onChange={(next) => update('hasMandatoryAddOns', next)}
          title={t('offers:admissionAddOnsMandatoryTitle')}
          subtitle={t('offers:admissionAddOnsMandatorySubtitle')}
          disabled={disabled}
          dataTestId={testId(dataTestId, 'mandatory-toggle')}
        />

        <SettingToggle
          value={value.addOnChoiceMode === SINGLE_CHOICE_ADDON}
          onChange={(next) => update('addOnChoiceMode', next ? SINGLE_CHOICE_ADDON : MULTIPLE_CHOICE_ADDON)}
          title={t('offers:admissionAddOnsLimitTitle')}
          subtitle={t('offers:admissionAddOnsLimitSubtitle')}
          disabled={disabled}
          dataTestId={testId(dataTestId, 'choice-mode-toggle')}
        />
      </div>
    </div>
  );

  const renderBody = () => {
    if (isLoading) return <Skeleton />;
    if (hiddenReason) return renderHidden();
    if (availableAddOns.length === 0) return renderEmptyState();
    return renderList();
  };

  const sectionBody = (
    <section
      data-testid={dataTestId}
      className={classNames('flex flex-col gap-4 w-full py-4 md:py-6', {
        'cursor-not-allowed opacity-50 pointer-events-none': disabled,
      })}>
      {renderBody()}
    </section>
  );

  return (
    <SectionDisclosure
      title={t('offers:admissionAddOnsSectionTitle')}
      open={open}
      onToggleOpen={onToggleOpen}
      dataTestId={`${dataTestId}-disclosure`}>
      {sectionBody}
    </SectionDisclosure>
  );
};
